<script setup lang="ts">
import { reactive, ref, watchEffect } from 'vue';

import {
  TSButtonSelectTree,
  TSCalendar,
  TSFilterContainer,
  TSMultiSelect,
  useToast,
} from 'tsv2-library';
import { MultiSelectOption } from 'tsv2-library/dist/src/types/options';

import AssetServices from '@/services/asset.service';

import { BorrowingRequestFilterParams } from '@/types/params.type';
import { BorrowingRequestFilters } from '@/types/filter.type';
import {
  BorrowingRequestFilterOptions,
  BorrowingRequestLoadingFilters,
} from '@/types/filterOptions.type';
import { BorrowingRequest } from '@/types/borrowing.type';

const props = defineProps<{
  assets: BorrowingRequest[];
  filterList: string[];
  name: string;
}>();

const emit = defineEmits<{
  applyFilter: [data: BorrowingRequestFilters];
}>();

const toast = useToast();

const filter = reactive<BorrowingRequestFilters>({});
const loadingFilter = ref<BorrowingRequestLoadingFilters>({});
const filterOptions = ref<BorrowingRequestFilterOptions>({});

const tagOptions: MultiSelectOption[] = [
  {
    label: 'RFID',
    value: 'RFID',
  },
  {
    label: 'QR',
    value: 'QR',
  },
  {
    label: 'Non-TAG',
    value: 'Non TAG',
  },
];

const getFilterOptions = async (
  optionField: keyof BorrowingRequestLoadingFilters,
): Promise<void> => {
  try {
    loadingFilter.value[optionField] = true;

    const { data } = await AssetServices.getAssetByIdOptions({
      [optionField]: true,
      _id: JSON.stringify(
        props.assets.map((asset) =>
          props.name === 'borrow-request-filter' ? asset._id : asset.asset,
        ),
      ),
    });

    filterOptions.value = {
      ...filterOptions.value,
      [optionField]: data.data[optionField],
    };
  } catch (error) {
    console.error(error);
    toast.add({
      error,
      message: `Error, failed to fetch ${optionField} filter options`,
    });
  } finally {
    loadingFilter.value[optionField] = false;
  }
};

/**
 * This function apply the selected filters
 */
const applyFilter = (): void => {
  emit('applyFilter', filter);
};

/**
 * This function clear the selected filters by looping the keys of selected filter and delete it.
 */
const clearFilter = (): void => {
  const keys = Object.keys(filter) as Array<keyof BorrowingRequestFilterParams>;

  keys.forEach((key) => {
    delete filter[key];
  });
};

/**
 * Watch for changed filters
 * - if the filter have no selected data, delete the field.
 */
watchEffect(() => {
  const fields = Object.keys(filter) as Array<
    keyof BorrowingRequestFilterParams
  >;

  fields.forEach((field) => {
    if (!filter[field]?.length) delete filter[field];
  });
});
</script>
<template>
  <TSFilterContainer
    :data-ts-name="name"
    @apply="applyFilter"
    @clear="clearFilter"
  >
    <TSMultiSelect
      v-if="filterList.includes('Name')"
      v-model="filter.name"
      :loading="loadingFilter.nameOptions"
      :options="filterOptions.nameOptions"
      @show="getFilterOptions('nameOptions')"
      data-ts-section="asset-name-filter-input"
      label="Name"
      option-label="label"
      option-value="label"
      placeholder="Select asset name"
    />
    <TSMultiSelect
      v-if="filterList.includes('TAG')"
      v-model="filter.tagType"
      :options="tagOptions"
      data-ts-section="tag-filter-input"
      label="TAG"
      option-label="label"
      option-value="value"
      placeholder="Select TAG"
    />
    <TSMultiSelect
      v-if="filterList.includes('Brand')"
      v-model="filter.brand"
      :loading="loadingFilter.brandOptions"
      :options="filterOptions.brandOptions"
      @show="getFilterOptions('brandOptions')"
      data-ts-section="brand-filter-input"
      label="Brand"
      option-label="label"
      option-value="label"
      placeholder="Select brand"
    />
    <TSMultiSelect
      v-if="filterList.includes('Model')"
      v-model="filter.model"
      :loading="loadingFilter.modelOptions"
      :options="filterOptions.modelOptions"
      @show="getFilterOptions('modelOptions')"
      data-ts-section="model-filter-input"
      label="Model/Type"
      option-label="label"
      option-value="label"
      placeholder="Select model/type"
    />
    <div
      v-if="filterList.includes('Group')"
      data-ts-section="group-filter-input"
    >
      <TSButtonSelectTree
        v-model:selected-nodes="filter.group"
        field-label="Group"
        type="Group"
      />
    </div>
    <TSCalendar
      v-if="filterList.includes('Due Return Date')"
      v-model="filter.dueDate"
      data-ts-section="due-date-filter-input"
      label="Due Return Date"
      mode="range"
    />
  </TSFilterContainer>
</template>
